<script lang="ts">
    import { untrack } from 'svelte';
    import { fade } from 'svelte/transition';
    import type { TileImage } from '@shared/types/types';
    import { getImageUrl } from './ImageUrlBuilder.ts';
    import type { ImageAspectRatio, ImageLoadingOptions } from './types';

    interface Props {
        image: TileImage;
        imageLoadingOptions: ImageLoadingOptions;
        variationId: string;
        detailPageLink: string;
        onClick?: (event: Event) => void;
        size?: { width: string; height: string };
        aspectRatio?: ImageAspectRatio;
        linkType?: 'image' | 'tile';
    }

    let {
        image,
        variationId,
        detailPageLink,
        onClick,
        imageLoadingOptions,
        size,
        aspectRatio,
        linkType = 'image',
    }: Props = $props();

    let loadingError = $state(false);

    const webpSrc = $derived(getImageUrl(image.id ?? '', 'webp', aspectRatio));
    const jpegSrc = $derived(getImageUrl(image.id ?? '', 'jpeg', aspectRatio));
    const imgSrc = $derived(loadingError ? (image.fallback ?? '') : jpegSrc);

    /**
 *
 *
 */
    let lastVariationId = $state(untrack(() => variationId));
    $effect(() => {
        if (variationId !== lastVariationId) {
            loadingError = false;
            lastVariationId = variationId;
        }
    });

    const imagePaddingTop = $derived.by(() => {
        if (!aspectRatio) return '100%';

        switch (aspectRatio) {
            case 'landscape':
            case 'landscape-two-column':
                return '69.5%';
            case 'portrait':
                return '143%';
            default:
                return '100%';
        }
    });
</script>

<div
    class="reptile-product-image"
    data-qa={`ftfind-product-image-${variationId}`}
    style:width={size?.width}
    style:height={size?.height}
    style:padding-top={imagePaddingTop}
>
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <oc-link-v2
        class="reptile-product-image__link"
        base64-href={btoa(detailPageLink)}
        onclick={onClick}
        data-link-type={linkType}
    >
        {#key variationId}
            <picture class="reptile-product-image__image" in:fade>
                {#if !loadingError}
                    <source srcset={webpSrc} type="image/webp" />
                    <source srcset={jpegSrc} type="image/jpeg" />
                {/if}
                <img
                    class="reptile-product-image__image"
                    src={imgSrc}
                    alt={image.alt ?? ''}
                    onerror={() => {
                        loadingError = true;
                    }}
                    loading={imageLoadingOptions.loading}
                    fetchpriority={imageLoadingOptions.fetchpriority}
                />
            </picture>
        {/key}
    </oc-link-v2>
</div>

<style lang="scss">
    @use '@otto-ec/design-tokens/tokens';

    .reptile-product-image {
        position: relative;

        &__link {
            user-select: none;

            position: absolute;
            inset: 0;

            display: block;

            width: 100%;
            padding: tokens.$oc-semantic-spacing-12 tokens.$oc-semantic-spacing-12 0;

            background-color: tokens.$oc-semantic-color-background-canvas;
        }

        &__image {
            display: block;

            width: 100%;
            height: 100%;

            object-fit: contain;
            object-position: center;
        }
    }
</style>
